import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Box, IconButton, Typography } from '@mui/material';
import { type ReactNode } from 'react';

import { useExpandableTitle } from './useExpandableTitle';
import { ArtifactStatusChip } from '../ArtifactStatusChip/ArtifactStatusChip';
import { PageHeaderBreadcrumb } from '../PageHeader/types';
import {
  ProjectDetailHeader,
  type ProjectDetailHeaderMetaItem,
} from '../ProjectDetailHeader/ProjectDetailHeader';

export interface ArtifactDetailHeaderProps {
  /** Stable id prefix for testids (shared with the breadcrumb header). */
  id: string;
  /** Breadcrumb trail; omit for hosts without a breadcrumb. */
  breadcrumbs?: PageHeaderBreadcrumb[];
  /** Artifact name rendered as the page title. */
  title?: string;
  /** While true the title renders as a skeleton and the chip is withheld. */
  isLoading?: boolean;
  /** Status (any casing) for the inline chip; omit to hide it. */
  status?: string;
  /** Testid for the title element. Defaults to `${id}-title`. */
  titleTestId?: string;
  /** Testid for the status chip. Defaults to `${id}-status`. */
  statusTestId?: string;
  /** Right-aligned actions on the title row (buttons, menus). */
  rightContent?: ReactNode;
  /** Single line between the title and the meta row — see
   *  {@link ProjectDetailHeader.subtitle}. */
  subtitle?: ReactNode;
  /** Condensed meta row beneath the title. Omit/empty to hide it. */
  metaItems?: ProjectDetailHeaderMetaItem[];
  /**
   * Opt in to the condensed header: the title stops at two lines with a clickable "…"
   * that opens the full name, long breadcrumb names are capped, the title row and its
   * actions do not wrap, and the meta icons sit in one fixed slot. Off by default, so a
   * page that does not pass it renders exactly as before.
   */
  condensed?: boolean;
  /** Accessible label of the "…" control that opens a title cut off at two lines. */
  expandTitleLabel?: string;
  /** Accessible label of the control that folds an opened title back to two lines. */
  collapseTitleLabel?: string;
}

/**
 * Detail-page header for project artifacts: breadcrumb, title with an
 * inline status chip, right-aligned actions, and the condensed meta row.
 * Composes {@link ProjectDetailHeader}; meta items stay domain-supplied.
 */
export const ArtifactDetailHeader = ({
  id,
  breadcrumbs,
  title,
  isLoading = false,
  status,
  titleTestId,
  statusTestId,
  rightContent,
  subtitle,
  metaItems,
  condensed = false,
  expandTitleLabel = 'Show full title',
  collapseTitleLabel = 'Show less',
}: ArtifactDetailHeaderProps) => {
  const { titleRef, isExpanded, visibleText, onToggle } = useExpandableTitle(title, condensed);
  const isCut = condensed && !isExpanded && visibleText !== null;

  const plainTitleSlot = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <Typography
        variant="h5"
        component="h1"
        data-testid={titleTestId ?? `${id}-title`}
        sx={{ fontWeight: 400, minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {title}
      </Typography>
      {status && (
        <ArtifactStatusChip status={status} data-testid={statusTestId ?? `${id}-status`} />
      )}
    </Box>
  );

  const condensedTitleSlot = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, minWidth: 0 }}>
        <Typography
          ref={titleRef}
          variant="h5"
          component="h1"
          data-testid={titleTestId ?? `${id}-title`}
          sx={{ fontWeight: 400, minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {/* A long name is cut where it stops fitting two lines, with the only
              ellipsis right after it, in the title's own type; clicking opens it. */}
          {isCut ? visibleText : title}
          {isCut && (
            <Typography
              component="button"
              type="button"
              variant="h5"
              onClick={onToggle}
              aria-expanded={false}
              aria-label={expandTitleLabel}
              data-testid={`${id}-title-toggle`}
              sx={{
                m: 0,
                p: 0,
                border: 0,
                background: 'none',
                cursor: 'pointer',
                fontWeight: 400,
                color: 'text.primary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              …
            </Typography>
          )}
          {/* Inline after the text, not beside it, so opening the title does not
              narrow it and re-wrap the lines already showing. */}
          {isExpanded && (
            <IconButton
              size="small"
              onClick={onToggle}
              aria-expanded
              aria-label={collapseTitleLabel}
              data-testid={`${id}-title-toggle`}
              sx={{ ml: 1, p: 0, verticalAlign: 'middle' }}
            >
              <ExpandLessIcon fontSize="small" />
            </IconButton>
          )}
        </Typography>
      </Box>
      {status && (
        <ArtifactStatusChip status={status} data-testid={statusTestId ?? `${id}-status`} />
      )}
    </Box>
  );

  return (
    <ProjectDetailHeader
      id={id}
      breadcrumbs={breadcrumbs}
      title={title}
      isTitleLoading={isLoading}
      titleSlot={isLoading ? undefined : condensed ? condensedTitleSlot : plainTitleSlot}
      rightContent={rightContent}
      subtitle={subtitle}
      metaItems={metaItems}
      condensed={condensed}
    />
  );
};
